import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { showNotificationError } from 'Shared/helpers';
import ColorConstants from 'Wpds/colors/ColorConstants';
import WpToastManager from 'Wpds/components/toast/WpToastManager';

import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { saveTemplate, getTemplateSections } from 'Shared/api/workspaces';
import Spinner from 'Shared/components/Spinner';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

import './SaveWorkspaceTemplateModal.scss';

type Props = {
  onToggle(): void;
};

function SaveWorkspaceTemplateModal({ onToggle }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workspaceTemplateSections, setWorkspaceTemplateSections] = useState(null);
  const [excludedUuids, setExcludedUuids] = useState([]);
  const handleCancelClick = () => onToggle();
  const workspaceId = useSelector(state => state.workspace.id);
  const workspaceIsGroup = useSelector(state => state.workspace.isGroup);

  useEffect(() => {
    async function callGetTemplateSections() {
      try {
        const response = await getTemplateSections({ workspaceId });
        setWorkspaceTemplateSections(response.data);
      } catch (e) {
        showNotificationError(e);
      } finally {
        setIsSubmitting(false);
      }
    }

    callGetTemplateSections();
  }, []);

  const handleSubmit = async values => {
    try {
      setIsSubmitting(true);
      await saveTemplate({
        workspaceId,
        name: values.template_name,
        isPublic: values.template_privacy === 'public',
        description: values.template_description,
        excludedItemUuids: excludedUuids,
        templateType: workspaceIsGroup ? 'team' : 'one_on_one',
      });
      WpToastManager.success('Template created');
      onToggle();
    } catch (e) {
      showNotificationError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addToExcludedUuids = uuid => {
    setExcludedUuids(existing_uuids => [...existing_uuids, uuid]);
  };

  const Section = ({ section }) => {
    return (
      <div className="mr-3">
        {section.recurrence && (
          <WpTooltip>
            <WpTooltipTrigger>
              <span className="mr-3">
                <WpIcon name="recycle" />
              </span>
            </WpTooltipTrigger>
            <WpTooltipContent>Recurring Topic</WpTooltipContent>
          </WpTooltip>
        )}
        <span className="deletebutton">
          <WpButton size="sm" variant="tertiary" onClick={() => addToExcludedUuids(section.uuid)}>
            <WpIcon name="trash" />
          </WpButton>
        </span>
      </div>
    );
  };

  const Topic = ({ topic }) => {
    return (
      <div className="SaveWorkspaceTemplateModal__sections__subtopic pl-2 pb-1 pt-1">
        <div>
          <span>
            <WpIcon name="check-circle" color={ColorConstants.gray[400]} />
          </span>
          <span className="ml-2">{topic.text}</span>
        </div>
        <div className="mr-3">
          {topic.recurrence && (
            <WpTooltip>
              <WpTooltipTrigger>
                <span className="mr-3">
                  <WpIcon name="recycle" />
                </span>
              </WpTooltipTrigger>
              <WpTooltipContent>Recurring Topic</WpTooltipContent>
            </WpTooltip>
          )}
          <span className="deletebutton">
            <WpButton size="sm" variant="tertiary" onClick={() => addToExcludedUuids(topic.uuid)}>
              <WpIcon name="trash" />
            </WpButton>
          </span>
        </div>
      </div>
    );
  };

  return (
    <WpModal isOpen size="lg" onToggle={onToggle}>
      <WpModalHeader>Create a new template</WpModalHeader>
      <WpModalBody>
        <WpForm id="save_workspace_template_modal_wp_form_wrapper" onSubmit={handleSubmit}>
          <div className="mb-3">
            This template is based on your current workspace. To edit the template, select sections to remove.
          </div>
          <div>
            <WpFormLayout>
              <WpTextField required id="template_name" label="Template name" placeholder="" />
              <WpTextField id="template_description" label="Description" placeholder="" />
            </WpFormLayout>
          </div>
          <div>
            <WpRadioGroup id="template_privacy" label="Where do you want to save this template?" defaultValue="public">
              <WpRadioInput
                id="private"
                label="Private Gallery"
                value="private"
                descriptionText="Only you will be able to see and reuse this template"
              />
              <WpRadioInput
                id="public"
                label="Public Gallery"
                value="public"
                descriptionText="Everyone at your company will be able to see and reuse this template"
              />
            </WpRadioGroup>
          </div>
          <div className="SaveWorkspaceTemplateModal__wrapper mt-4">
            <h4 className="pt-3 pl-3">Discussion Topics</h4>
            {!workspaceTemplateSections && <Spinner activeIf className="my-5 h-100" />}
            {workspaceTemplateSections && (
              <div className="SaveWorkspaceTemplateModal__sections pt-2 pl-3 pr-5">
                {workspaceTemplateSections
                  .filter(section => !excludedUuids.includes(section.uuid))
                  .map(section => (
                    <div
                      key={section.uuid || section.name}
                      className="SaveWorkspaceTemplateModal__sections__topic_and_subtopics mb-3"
                    >
                      <div className="SaveWorkspaceTemplateModal__sections__container pl-2 pt-2">
                        <div>
                          <span className="SaveWorkspaceTemplateModal__sections__container__name">{section.name}</span>
                          <div className="SaveWorkspaceTemplateModal__sections__container__description mb-2 mr-1">
                            {section.description}
                          </div>
                        </div>
                        <Section section={section} />
                      </div>
                      {(section.discussion_topics_draft_js || [])
                        .filter(discussionTopic => !excludedUuids.includes(discussionTopic.uuid))
                        .map(discussionTopic => (
                          <Topic topic={discussionTopic} key={discussionTopic.uuid} />
                        ))}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </WpForm>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={handleCancelClick}>
            Cancel
          </WpButton>
          {/*  TODO: make it so that WPForm can enclose the entire modal, so we don't need this workaround  */}
          <WpButton
            onClick={() => {
              const element = document.createElement('button') as HTMLButtonElement;
              const divElement = document.getElementById(
                'save_workspace_template_modal_wp_form_wrapper'
              ) as HTMLDivElement;
              const formElement = divElement.firstChild as HTMLFormElement;
              element.setAttribute('type', 'submit');
              formElement.appendChild(element);
              element.click();
              element.remove();
            }}
            loading={isSubmitting}
          >
            Submit
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default SaveWorkspaceTemplateModal;
